// Piezas de permisos y módulos para pantallas (DS §6.41): ModuleGate, Can, estado de módulo apagado.
import { useEffect, type ReactNode } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';
import { PowerOff } from 'lucide-react';
import { EmptyState } from '@/components/ui/EmptyState';
import { toast, type ToastTone } from '@/components/ui/Toaster';
import { useModules, MODULE_LABELS, type ModuleKey } from '@/lib/modules';
import { useCan, useRole, type Action, type CanOptions } from '@/lib/permissions';
import { fill, t } from '@/lib/i18n/es-AR';

/** Estado de página "{Módulo} está apagado" (UX §15.4). */
export function ModuleOffState({ module, size = 'page' }: { module: ModuleKey; size?: 'page' | 'panel' }) {
  const role = useRole();
  const navigate = useNavigate();
  const { locationSlug } = useParams();
  return (
    <EmptyState
      size={size}
      icon={PowerOff}
      title={fill(t.moduleOff.title, { module: MODULE_LABELS[module] })}
      description={role === 'owner' ? t.moduleOff.owner : t.moduleOff.others}
      action={
        role === 'owner'
          ? { label: t.moduleOff.ownerAction, onClick: () => navigate(`/app/${locationSlug ?? ''}/ajustes/modulos`) }
          : undefined
      }
    />
  );
}

export interface ModuleGateProps {
  module: ModuleKey;
  children: ReactNode;
  /** `state` muestra el estado de módulo apagado; `hide` no renderiza nada (botones, secciones). */
  fallback?: 'state' | 'hide' | ReactNode;
}

/** Renderiza `children` solo si el módulo (y lo que requiere) está prendido. */
export function ModuleGate({ module, children, fallback = 'hide' }: ModuleGateProps) {
  const { isOn } = useModules();
  if (isOn(module)) return <>{children}</>;
  if (fallback === 'hide') return null;
  if (fallback === 'state') return <ModuleOffState module={module} />;
  return <>{fallback}</>;
}

export interface CanProps extends CanOptions {
  action: Action;
  children: ReactNode;
  fallback?: ReactNode;
}

/** `<Can action="sale.void">…</Can>`: oculta lo que el rol nunca puede hacer (UX §15.5). */
export function Can({ action, children, fallback = null, ...opts }: CanProps) {
  const can = useCan();
  return <>{can(action, opts) ? children : fallback}</>;
}

/** Redirige mostrando un aviso (rutas no permitidas, UX-P29). */
export function RedirectWithToast({ to, message, tone = 'warning' }: { to: string; message: string; tone?: ToastTone }) {
  useEffect(() => {
    toast[tone === 'booking' ? 'info' : tone](message, { id: `redirect-${message}` });
  }, [message, tone]);
  return <Navigate to={to} replace />;
}
